import Summary from '@/modules/common/hiring/models/APP/Summary'
import CommonMapper from '@/modules/core/mappers/CommonMapper'
import type {
  OptionsValuesInterface,
  SummaryType1Interface,
  SummaryType3Interface
} from '@/modules/core/models/types'
import { format } from '@/utils/date.utils'

import CoverageBasic from '@/modules/common/common/models/APP/CoverageBasic'
import type { PricePeriodType } from '@/modules/common/common/models/APP/types'
import { useSummaryComposable } from '@/modules/core/composables/useSummaryComposable'
import { UserRoleEnum } from '@/modules/core/models/enums'
import type LifeConfigurations from '@/modules/life/common/models/APP/LifeConfigurations'
import BENEFICIARY_TYPES from '@/modules/life/hiring/constants/BeneficiaryTypes'
import { addCommas, priceFormat } from '@/utils/textUtils'
import LifeHiringAnswers from './LifeHiringAnswers'

type i18nT = (key: string, values?: any) => string
export default class LifeHiringSummary implements Summary {
  private answers: LifeHiringAnswers
  private t
  private configurations: LifeConfigurations
  private coverages: CoverageBasic[]
  private professions: OptionsValuesInterface[]
  private activities: OptionsValuesInterface[]
  private summaryComposable

  constructor(
    data: LifeHiringAnswers,
    t: i18nT,
    configurations: LifeConfigurations,
    coverages: CoverageBasic[],
    professions: OptionsValuesInterface[],
    activities: OptionsValuesInterface[]
  ) {
    this.answers = data
    this.coverages = coverages
    this.configurations = configurations
    this.professions = professions
    this.activities = activities
    this.t = t
    this.summaryComposable = useSummaryComposable(t)
  }

  private getInsuredLabel(role: string): string {
    if (role === UserRoleEnum.HOLDER_INSURED) {
      return 'holder-insured'
    }
    if (role === UserRoleEnum.HOLDER_NOT_INSURED) {
      return 'holder'
    }
    return 'insured'
  }

  private getIndex(role: string, index: number, holderIsNotInsured: boolean): number {
    if (holderIsNotInsured && role === UserRoleEnum.INSURED) {
      return index
    }
    return index + 1
  }

  #getStep0Summary(): SummaryType1Interface[] {
    const insured = this.answers.insureds[0]
    const birthDate = format(insured.userDetails.birthDate)

    const capital = insured.additionalCoverages?.[0]?.value ?? 0
    const capitalWithCommas = addCommas(capital)

    const coveragesMandatory: string[] = []
    const coverages: string[] = []
    insured.additionalCoverages.forEach((c) => {
      const coverage = this.coverages.find(({ coverageCode }) => coverageCode === c.key)
      if (coverage) {
        if (coverage.mandatory) {
          coveragesMandatory.push(coverage.title)
        } else {
          coverages.push(coverage.title)
        }
      }
    })

    return [
      {
        label: this.t(`copy_summary_step0_date_label`),
        value: format(this.answers.startDate),
        visible: !!this.answers.startDate
      },
      {
        label: this.t(`copy_summary_step0_billing_label`),
        value: this.answers.billingCycle
          ? CommonMapper.toDXPProductPrice(this.answers.billingCycle)
          : '',
        visible: !!this.answers.billingCycle
      },
      {
        label: this.t(`copy_summary_step0_num-insureds_label`),
        value: this.t('components.InsuredSubheaderComponent.label', 1),
        visible: true
      },

      {
        label: this.t(`copy_summary_step0_birth-dates_label`, 1),
        value: birthDate,
        visible: !!birthDate
      },
      {
        label: this.t(`copy_summary_step0_insured-capital_label`),
        value: (capitalWithCommas || '---') + '€',
        visible: true
      },
      {
        label: this.t(`copy_summary_step0_reevaluation-type_label`),
        value: this.answers.reevaluationType?.description ?? '',
        visible: !!this.answers.reevaluationType
      },
      /*{
        label: this.t(`copy_summary_step0_insured-mandatory-coverages_label`),
        value: coveragesMandatory.join(', '),
        visible: !!coveragesMandatory.length
      },*/
      {
        label: this.t(`copy_summary_step0_insured-optional-coverages_label`),
        value: coverages.join(', '),
        visible: !!coverages.length
      }
    ]
  }
  capitalizeFirstLetter(input: string): string {
    if (!input) return ''
    return input.charAt(0).toUpperCase() + input.slice(1).toLowerCase()
  }

  #getProfessionData(): SummaryType1Interface[] {
    const holder = this.answers.holder
    const profession = this.professions.find((p) => p.value === holder.profession)
    const activity = this.activities.find((a) => a.value === holder.activity)

    return [
      {
        label: this.t(`copy_summary-step_profession-data_profession-label`),
        value: this.capitalizeFirstLetter(String(profession?.label)),
        visible: !!profession
      },
      {
        label: this.t(`copy_summary-step_profession-data_activity-label`),
        value: this.capitalizeFirstLetter(String(activity?.label)),
        visible: !!activity
      }
    ]
  }

  #getInsuredsData(subStepIndex: number): SummaryType3Interface[] {
    const insureds = [...this.answers.insureds]
    if (this.answers.holder.role === UserRoleEnum.HOLDER_NOT_INSURED) {
      insureds.unshift(this.answers.holder)
    }

    return insureds.map((insured, i) => {
      const { userDetails, role } = insured

      const i18nLabel = this.getInsuredLabel(role)
      const labelInsured = this.t(`hiring.common.${i18nLabel}-without-n`)
      const label = userDetails.name ? userDetails.getFullName() : ''
      const page = `Step2_${i}_1_InsuranceAnotherCompanyDataForm`

      const subStep1 = this.summaryComposable.getDocumentInsuredStep(insured)
      const subStep2 = this.summaryComposable.getAddressStep(insured)
      const subStep3 = [
        this.summaryComposable.getEmailStep(insured),
        this.summaryComposable.getPhoneStep(insured)
      ]
      const stepsToFilter = [subStep1, subStep2, subStep3]
      const currentRiskNumber = this.answers.currentInsured?.riskNumber
      const subSteps = this.summaryComposable.filterInsuredsRenderableSteps(
        insured,
        currentRiskNumber!,
        stepsToFilter,
        subStepIndex
      )

      return {
        label: label,
        visible: subSteps.length > 0 && !!label,
        page,
        subLabel: labelInsured,
        values: subSteps
      }
    })
  }

  #getQuestionnaireData() {
    const i18nRoot = 'copy_summary-step_questionnaire-data'

    const insured = this.answers.insureds[0]
    const bodyMeasureVisible = insured.userDetails.weight && insured.userDetails.height
    const medicalSituationVisible = insured.questionnaire

    return [
      {
        label: this.t(`${i18nRoot}_measures-label`),
        visible: bodyMeasureVisible,
        page: 'Step3_1_InsuranceBodyMeasurementsForm'
      },
      {
        label: this.t(`${i18nRoot}_medical-situation-label`),
        visible: medicalSituationVisible,
        page: 'Step3_2_MedicalQuestionnaireForm'
      }
    ]
  }

  #getMortgageData(): SummaryType1Interface[] {
    const insured = this.answers.insureds[0]
    const mortgageClause = insured.mortgageClauses[0]

    if (!mortgageClause) {
      return []
    }

    const i18nRoot = 'copy_summary-step_mortgage-data'
    const [year, month] = mortgageClause.expirationDate.split('-')

    return [
      {
        label: this.t(`${i18nRoot}_entity-title`),
        value: mortgageClause.entity,
        visible: true
      },
      {
        label: this.t(`${i18nRoot}_loan-number-title`),
        value: mortgageClause.loanNumber,
        visible: true
      },
      {
        label: this.t(`${i18nRoot}_expiration-title`),
        value: `${month}/${year}`,
        visible: true
      }
    ]
  }

  #hasCustomBeneficiaries() {
    const beneficiaries = this.answers.insureds[0].beneficiaries

    return beneficiaries?.some((b) => b.beneficiaryType === BENEFICIARY_TYPES.CUSTOM) ?? false
  }

  #getBeneficiariesData() {
    try {
      const i18nRoot = 'copy_summary-step_beneficiaries-data'

      const isCustom = this.#hasCustomBeneficiaries()
      if (!isCustom) {
        return [
          {
            label: this.t(`${i18nRoot}_legal-beneficiaries-label`),
            value: this.t(`${i18nRoot}_legal-beneficiaries`),
            visible: true
          }
        ]
      }

      const insured = this.answers.insureds[0]
      const beneficiaries = insured.beneficiaries
      return beneficiaries.map((beneficiary, i) => {
        const subLabel = `${this.t(`${i18nRoot}_beneficiary-sub-label`)} ${i + 1}`
        const isMinor = beneficiary.documentType === 'MENOR'
        const hasDocumentNumber = !!beneficiary.documentNumber
        const isVisible = hasDocumentNumber || isMinor
        const value = isMinor ? beneficiary.documentTypeText : beneficiary.documentNumber
        return {
          label: beneficiary.getFullName(),
          visible: !!beneficiary,
          page: 'Step4_3_BeneficiaryTypeForm',
          subLabel,
          values: [
            {
              label: 'Nº de documento',
              value,
              visible: isVisible
            },
            {
              label: this.t(`${i18nRoot}_beneficiary-percentage-label`),
              value: `${beneficiary.distributionPercentage}%`,
              visible: true
            }
          ]
        }
      })
    } catch (e) {
      console.log(e)
    }
  }

  getProductName() {
    return this.answers.product?.title ?? ''
  }

  getPrice() {
    const product = this.answers.product
    const price = priceFormat(this.answers.price)

    let billingCycle = null
    if (this.configurations && this.answers.billingCycle) {
      billingCycle = `/${this.configurations.billingCycles[
        this.answers.billingCycle as PricePeriodType
      ]?.textInPrice}`
    }

    return `${price}${product?.units}${billingCycle}`
  }

  getRequestNumber() {
    return String(this.answers.product?.quotationNumber)
  }

  getStepsData(subStepIndex: number) {
    const hasCustomBeneficiaries = this.#hasCustomBeneficiaries()

    return [
      {
        label: this.t('copy_summary-step_insurance-data-title'),
        type: 'TYPE1',
        items: this.#getStep0Summary(),
        page: 'Step0_1_StartDateForm'
      },
      {
        label: this.t('copy_summary-step_profession-data-title'),
        type: 'TYPE1',
        items: this.#getProfessionData(),
        page: 'Step1_1_ProfessionAndActivityForm'
      },
      {
        label: this.t('copy_summary-step_personal-data-title'),
        type: 'TYPE3',
        items: this.#getInsuredsData(subStepIndex),
        page: 'Step2_0_1_InsuranceAnotherCompanyDataForm'
      },
      {
        label: this.t('copy_summary-step_questionnaire-data-title'),
        type: 'TYPE3', //1
        items: this.#getQuestionnaireData(),
        page: 'Step3_1_InsuranceBodyMeasurementsForm'
      },
      {
        label: this.t('copy_summary-step_mortgage-data-title'),
        type: 'TYPE1',
        items: this.#getMortgageData(),
        page: 'Step4_1_HasMortgageForm'
      },
      {
        label: this.t('copy_summary-step_beneficiaries-data-title'),
        type: hasCustomBeneficiaries ? 'TYPE3' : 'TYPE1',
        items: this.#getBeneficiariesData(),
        page: 'Step4_3_BeneficiaryTypeForm'
      },
      {
        label: this.t('hiring.summary.health.step4.label'),
        type: 'TYPE1',
        items: [],
        page: 'Step5_1_BankAccountForm'
      }
    ]
  }
}
